import React from "react";
import { HelpCircle, MessageCircle, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { FAQ_ITEMS } from "../data/faq";
import { useLang } from "../context/LangContext";
import { PHONE, PHONE_TEL } from "../lib/assets";

const FAQ = () => {
  const { lang, t } = useLang();
  const items = FAQ_ITEMS[lang] || FAQ_ITEMS.ro;

  return (
    <section id="faq" data-testid="faq-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left side — heading + side card */}
          <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-orange/15 mb-5">
              <HelpCircle className="w-3.5 h-3.5 text-brand-orange-deep" />
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-brand-orange-deep">
                {t.faq.eyebrow}
              </span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-navy leading-tight tracking-tight">
              {t.faq.title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
              {t.faq.subtitle}
            </p>

            <div className="mt-8 bg-brand-navy text-white rounded-3xl p-6 sm:p-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-brand-orange/20 text-brand-orange flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <p className="font-display font-bold">{t.faq.cardTitle}</p>
              </div>
              <p className="text-sm text-white/75 leading-relaxed mb-5">
                {t.faq.cardDesc}
              </p>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="faq-call-btn"
                className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-5 py-3 rounded-full transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                {PHONE}
              </a>
            </div>
          </div>

          {/* Right side — accordion */}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
              {items.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="bg-brand-cream rounded-2xl border border-brand-border-soft px-5 sm:px-6 [&>h3]:flex"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="hover:no-underline py-5 text-left font-display text-base sm:text-lg font-bold text-brand-navy [&[data-state=open]>svg]:text-brand-orange-deep gap-4"
                  >
                    <span className="flex items-start gap-3 sm:gap-4 flex-1">
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-orange/15 text-brand-orange-deep font-display text-xs sm:text-sm font-extrabold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="leading-snug pt-0.5">{item.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${i}`}
                    className="pb-6 pt-1 text-brand-muted text-sm sm:text-base leading-relaxed space-y-3 pl-10 sm:pl-12"
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
